/**
 * Public landing page — AI Act Self-Assessment lead magnet.
 * Route: /landing/ai-act (no authentication required).
 *
 * 5 diagnostic questions -> regulatory exposure score (0-100) + tailored
 * findings/recommendations. Captures name/email/company as a lead in
 * db.leads. Distinctive dark aesthetic to stand apart from the admin app.
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ShieldCheck, Brain, Sparkles, CheckCircle2, AlertTriangle, ChevronRight, Loader2, Mail, Building2, User as UserIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HIGH_RISK_DOMAINS = [
  { id: 'biometrics', label: 'Biometria & reconhecimento facial', hint: 'Anexo III §1' },
  { id: 'critical_infrastructure', label: 'Infraestrutura crítica (energia, transportes)', hint: 'Anexo III §2' },
  { id: 'education', label: 'Educação & formação profissional', hint: 'Anexo III §3' },
  { id: 'employment_hr', label: 'Recursos Humanos & recrutamento', hint: 'Anexo III §4' },
  { id: 'essential_services', label: 'Acesso a serviços essenciais (crédito, seguros, benefícios)', hint: 'Anexo III §5' },
  { id: 'law_enforcement', label: 'Forças de segurança & prevenção criminal', hint: 'Anexo III §6' },
  { id: 'migration_border', label: 'Migração, asilo & controlo de fronteiras', hint: 'Anexo III §7' },
  { id: 'justice', label: 'Administração da justiça & processos democráticos', hint: 'Anexo III §8' },
];

const LEVEL_UI = {
  critical: { badge: 'bg-red-500/20 text-red-300 border-red-500/40', ring: 'ring-red-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(239,68,68,0.5)]', accent: 'text-red-400', label: 'CRÍTICO' },
  high:     { badge: 'bg-orange-500/20 text-orange-300 border-orange-500/40', ring: 'ring-orange-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(249,115,22,0.5)]', accent: 'text-orange-400', label: 'ALTO' },
  medium:   { badge: 'bg-amber-500/20 text-amber-300 border-amber-500/40', ring: 'ring-amber-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(245,158,11,0.4)]', accent: 'text-amber-400', label: 'MODERADO' },
  low:      { badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', ring: 'ring-emerald-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(16,185,129,0.5)]', accent: 'text-emerald-400', label: 'BAIXO' },
};

export default function AiActLanding() {
  const [step, setStep] = useState(0); // 0 = hero, 1..5 = questions, 6 = contact form, 7 = result
  const [answers, setAnswers] = useState({
    uses_ai: '',
    high_risk_domains: [],
    eu_users: '',
    has_tech_doc: '',
    has_human_oversight: '',
  });
  const [contact, setContact] = useState({ name: '', email: '', company: '', role: '', consent_marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [abVariant, setAbVariant] = useState('A');

  // Assign A/B variant on first visit (50/50 sticky per browser).
  // Uses a separate key from NIS2 so both landings can be tested independently.
  useEffect(() => {
    try {
      const KEY = 'nis2ai_aiact_ab_variant';
      let v = localStorage.getItem(KEY);
      const forced = new URLSearchParams(window.location.search).get('ab');
      if (forced && ['A', 'B'].includes(forced.toUpperCase())) {
        v = forced.toUpperCase();
        localStorage.setItem(KEY, v);
      } else if (!v) {
        v = Math.random() < 0.5 ? 'A' : 'B';
        localStorage.setItem(KEY, v);
      }
      setAbVariant(v);
    } catch { /* private mode: default to A */ }
  }, []);

  useEffect(() => { document.title = 'NIS2AI · Self-Assessment EU AI Act (grátis)'; }, []);
  useMetaTags({
    title: 'Self-Assessment EU AI Act (grátis · 2 min) — NIS2AI',
    description: 'Descobre em 5 perguntas se a tua organização está exposta ao Regulamento UE 2024/1689. Score de exposição regulatória + plano personalizado gerado por IA.',
  });

  const totalSteps = 5;
  const progress = useMemo(() => {
    if (step === 0) return 0;
    if (step >= 6) return 100;
    return Math.round(((step) / totalSteps) * 100);
  }, [step]);

  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return !!answers.uses_ai;
      case 2: return true; // multi-select can be empty
      case 3: return !!answers.eu_users;
      case 4: return !!answers.has_tech_doc;
      case 5: return !!answers.has_human_oversight;
      case 6: return contact.name.trim().length >= 2 && /.+@.+\..+/.test(contact.email) && contact.company.trim().length >= 1;
      default: return true;
    }
  }, [step, answers, contact]);

  const toggleDomain = (id) => {
    setAnswers(prev => ({
      ...prev,
      high_risk_domains: prev.high_risk_domains.includes(id)
        ? prev.high_risk_domains.filter(x => x !== id)
        : [...prev.high_risk_domains, id],
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let utm_source;
      try {
        const raw = localStorage.getItem('nis2ai_utm');
        if (raw) utm_source = (JSON.parse(raw).utm_source || undefined);
      } catch { /* noop */ }
      const { data } = await axios.post(`${BACKEND_URL}/api/public/ai-act/self-assessment`, {
        ...contact,
        answers,
        ab_variant: abVariant,
        utm_source,
      });
      setResult(data);
      setStep(7);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível submeter. Tente novamente em instantes.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-slate-100 relative overflow-hidden">
      {/* Ambient decorations */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-[-20%] left-[-10%] h-[520px] w-[520px] rounded-full bg-indigo-600/20 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] h-[520px] w-[520px] rounded-full bg-fuchsia-600/20 blur-[140px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[900px] rounded-full bg-cyan-500/10 blur-[100px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 [background:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:32px_32px] opacity-40" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <Link to="/landing/ai-act" className="flex items-center gap-2 font-semibold tracking-tight" data-testid="landing-logo">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
            <Brain className="h-4 w-4" />
          </div>
          <span className="text-lg">NIS2<span className="text-indigo-400">AI</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/landing/nis2" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline" data-testid="landing-crosslink-nis2">
            Também para NIS2 →
          </Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="landing-login-link">Entrar</Link>
          <Button
            onClick={() => setStep(1)}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-medium"
            data-testid="landing-cta-nav"
          >
            Iniciar diagnóstico <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {step === 0 && <Hero onStart={() => setStep(1)} variant={abVariant} />}
        {step >= 1 && step <= 5 && (
          <div className="max-w-3xl mx-auto" data-testid="assessment-panel">
            <ProgressBar progress={progress} step={step} total={totalSteps} />
            {step === 1 && <Q1 value={answers.uses_ai} onChange={(v) => setAnswers({ ...answers, uses_ai: v })} />}
            {step === 2 && <Q2 selected={answers.high_risk_domains} onToggle={toggleDomain} />}
            {step === 3 && <Q3 value={answers.eu_users} onChange={(v) => setAnswers({ ...answers, eu_users: v })} />}
            {step === 4 && <Q4 value={answers.has_tech_doc} onChange={(v) => setAnswers({ ...answers, has_tech_doc: v })} />}
            {step === 5 && <Q5 value={answers.has_human_oversight} onChange={(v) => setAnswers({ ...answers, has_human_oversight: v })} />}
            <StepNav
              onBack={() => setStep(step - 1)}
              onNext={() => setStep(step + 1)}
              canProceed={canProceed}
              nextLabel={step === 5 ? 'Ver resultado →' : 'Continuar'}
              nextTestId={`assessment-next-${step}`}
              backTestId={`assessment-back-${step}`}
            />
          </div>
        )}
        {step === 6 && (
          <ContactForm
            contact={contact}
            setContact={setContact}
            onBack={() => setStep(5)}
            onSubmit={handleSubmit}
            submitting={submitting}
            canProceed={canProceed}
          />
        )}
        {step === 7 && result && (
          <ResultView result={result} onRestart={() => { setStep(0); setResult(null); setAnswers({ uses_ai: '', high_risk_domains: [], eu_users: '', has_tech_doc: '', has_human_oversight: '' }); setContact({ name: '', email: '', company: '', role: '', consent_marketing: false }); }} />
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-6 text-center text-xs text-slate-500">
        <p>
          © {new Date().getFullYear()} NIS2AI · Plataforma multi-tenant de conformidade NIS2 &amp; EU AI Act ·
          {' '}<Link to="/trust" className="hover:text-white underline underline-offset-2" data-testid="landing-footer-trust-link">Trust Center</Link>
          {' · '}
          <Link to="/insights" className="hover:text-white underline underline-offset-2" data-testid="landing-footer-insights-link">Insights</Link>
          {' · '}
          <Link to="/login" className="hover:text-white underline underline-offset-2">Entrar</Link>
        </p>
        <p className="mt-1 text-[10px] text-slate-600">O diagnóstico é indicativo, não substitui aconselhamento jurídico especializado.</p>
      </footer>
    </div>
  );
}

// ============================================================================
// Hero
// ============================================================================
function Hero({ onStart, variant = 'A' }) {
  const isB = variant === 'B';
  const badge = isB
    ? 'Coimas até 35 M€ ou 7% · Regulamento UE 2024/1689'
    : 'Diagnóstico gratuito · gerado em 2 min · sem instalação';
  const headlineTop = isB ? 'Coimas até' : 'A sua empresa está pronta para o';
  const headlineBottom = isB ? '35 M€ com o EU AI Act' : 'EU AI Act?';
  const sublineB = 'Descubra em 2 minutos se algum sistema seu cai em risco elevado ou GPAI — e receba um plano de mitigação priorizado. O Regulamento UE 2024/1689 entra em plena aplicação em Agosto de 2026.';
  const ctaLabel = isB ? 'Ver o meu risco AI Act' : 'Iniciar diagnóstico gratuito';
  return (
    <div className="text-center max-w-3xl mx-auto" data-testid="landing-hero" data-ab-variant={variant}>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-xs text-slate-300 mb-8">
        <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
        {badge}
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
        {headlineTop}
        <span className="block bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent"> {headlineBottom}</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
        {isB ? sublineB : (<>Responda a <b className="text-slate-200">5 perguntas</b> e receba um score de exposição regulatória, os principais gaps identificados e um plano personalizado — construído com base no <b className="text-slate-200">Regulamento UE 2024/1689</b> e Anexos III/IV.</>)}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={onStart}
          size="lg"
          className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold px-8 h-12 text-base"
          data-testid="landing-start-btn"
        >
          {ctaLabel} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <a
          href="#how"
          className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1"
        >
          Como funciona <ChevronRight className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Value props */}
      <div id="how" className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
        {[
          { icon: ShieldCheck, title: '13 sub-módulos AI Act', body: 'Cobre integralmente Arts. 9, 10, 11, 12, 13, 14, 43, 49, 51-56, 72 e 73 do Regulamento UE 2024/1689.' },
          { icon: Brain, title: 'Motor Claude Sonnet 4.6', body: 'Auto-drafting de doc técnica, análise de riscos e geração de roadmap alimentados por IA de topo.' },
          { icon: CheckCircle2, title: 'Preparado NIS2 · Lei 125/2025', body: 'Módulo NIS2 opcional para entidades cobertas pela Diretiva UE 2022/2555 e transposição PT.' },
        ].map((c, i) => (
          <div key={i} className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md hover:bg-white/[0.04] transition-colors">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 flex items-center justify-center mb-4">
              <c.icon className="h-5 w-5 text-indigo-400" />
            </div>
            <h3 className="font-semibold text-slate-100">{c.title}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Progress + StepNav
// ============================================================================
function ProgressBar({ progress, step, total }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
        <span>Pergunta {step} de {total}</span>
        <span>{progress}%</span>
      </div>
      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-indigo-400 to-fuchsia-400 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function StepNav({ onBack, onNext, canProceed, nextLabel, nextTestId, backTestId }) {
  return (
    <div className="mt-10 flex items-center justify-between">
      <button
        onClick={onBack}
        className="text-sm text-slate-400 hover:text-white transition-colors"
        data-testid={backTestId}
      >
        ← Voltar
      </button>
      <Button
        onClick={onNext}
        disabled={!canProceed}
        className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={nextTestId}
      >
        {nextLabel} <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
    </div>
  );
}

// ============================================================================
// Question components
// ============================================================================
function QuestionShell({ title, subtitle, children }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight leading-tight">{title}</h2>
        {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function ChoiceRadio({ value, onChange, options, testIdPrefix }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
      {options.map(opt => (
        <label
          key={opt.value}
          htmlFor={`${testIdPrefix}-${opt.value}`}
          className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
            value === opt.value
              ? 'border-indigo-400 bg-indigo-500/10 shadow-[0_0_40px_-15px_rgba(129,140,248,0.5)]'
              : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
          }`}
        >
          <RadioGroupItem value={opt.value} id={`${testIdPrefix}-${opt.value}`} data-testid={`${testIdPrefix}-${opt.value}`} className="mt-0.5 border-slate-500 text-indigo-400" />
          <div className="flex-1">
            <p className="font-medium text-slate-100">{opt.label}</p>
            {opt.hint && <p className="text-xs text-slate-400 mt-0.5">{opt.hint}</p>}
          </div>
        </label>
      ))}
    </RadioGroup>
  );
}

function Q1({ value, onChange }) {
  return (
    <QuestionShell
      title="A vossa organização utiliza ou desenvolve sistemas de IA?"
      subtitle="Inclui modelos próprios, ferramentas SaaS com IA embutida, chatbots, sistemas de recomendação, computer vision, etc."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q1"
        options={[
          { value: 'yes', label: 'Sim — em produção', hint: 'Já operamos sistemas IA em ambiente real' },
          { value: 'planning', label: 'Planeamos adotar nos próximos 12 meses', hint: 'Em piloto ou POC' },
          { value: 'no', label: 'Não utilizamos', hint: 'Sem sistemas IA operacionais' },
        ]}
      />
    </QuestionShell>
  );
}

function Q2({ selected, onToggle }) {
  return (
    <QuestionShell
      title="Em que domínios operam esses sistemas de IA?"
      subtitle="Selecione todos os que se aplicam. Estes domínios estão listados no Anexo III do EU AI Act como high-risk."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {HIGH_RISK_DOMAINS.map(d => {
          const isSelected = selected.includes(d.id);
          return (
            <label
              key={d.id}
              className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                isSelected
                  ? 'border-fuchsia-400 bg-fuchsia-500/10 shadow-[0_0_30px_-15px_rgba(232,121,249,0.5)]'
                  : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
              }`}
              data-testid={`q2-${d.id}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggle(d.id)}
                className="mt-0.5 border-slate-500 data-[state=checked]:bg-fuchsia-500 data-[state=checked]:border-fuchsia-500"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-100 text-sm">{d.label}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{d.hint}</p>
              </div>
            </label>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-3">Nenhum se aplica? Deixe em branco e continue.</p>
    </QuestionShell>
  );
}

function Q3({ value, onChange }) {
  return (
    <QuestionShell
      title="Os vossos sistemas IA interagem com utilizadores ou dados da UE?"
      subtitle="O AI Act tem efeito extraterritorial (Art. 2): aplica-se independentemente do país de sede se a saída da IA for usada na UE."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q3"
        options={[
          { value: 'yes', label: 'Sim', hint: 'Utilizadores, dados pessoais ou clientes na UE' },
          { value: 'unsure', label: 'Não temos a certeza', hint: 'Precisamos de confirmar com o DPO' },
          { value: 'no', label: 'Não', hint: 'Operações exclusivamente fora da UE' },
        ]}
      />
    </QuestionShell>
  );
}

function Q4({ value, onChange }) {
  return (
    <QuestionShell
      title="Têm documentação técnica formal dos vossos sistemas IA?"
      subtitle="Cobrindo as 9 secções do Anexo IV do AI Act: descrição, propósito, arquitetura, dados de treino, métricas, riscos residuais, etc."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q4"
        options={[
          { value: 'yes', label: 'Sim — cobertura completa', hint: 'Todas as 9 secções documentadas e revistas' },
          { value: 'partial', label: 'Parcial', hint: 'Algumas secções cobertas, outras em falta' },
          { value: 'no', label: 'Não', hint: 'Documentação técnica formal inexistente ou dispersa' },
        ]}
      />
    </QuestionShell>
  );
}

function Q5({ value, onChange }) {
  return (
    <QuestionShell
      title="Existem controlos de supervisão humana sobre as decisões IA?"
      subtitle="Art. 14: mecanismos para prevenir/minimizar riscos, incluindo override manual, botão de paragem, e explicabilidade das saídas."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q5"
        options={[
          { value: 'yes', label: 'Sim — controlos implementados', hint: 'Override, stop button, training de operadores, explicabilidade' },
          { value: 'partial', label: 'Alguns controlos', hint: 'Cobertura parcial dos 6 controlos-base' },
          { value: 'no', label: 'Não', hint: 'Decisões IA operam sem supervisão humana formal' },
        ]}
      />
    </QuestionShell>
  );
}

// ============================================================================
// Contact form
// ============================================================================
function ContactForm({ contact, setContact, onBack, onSubmit, submitting, canProceed }) {
  return (
    <div className="max-w-2xl mx-auto" data-testid="contact-form">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-300 mb-6">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Respostas registadas — quase pronto
        </div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Onde enviamos o seu diagnóstico?</h2>
        <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto">
          Vamos calcular a exposição regulatória e apresentar recomendações personalizadas. Sem spam, prometido.
        </p>
      </div>

      <div className="space-y-4 bg-white/[0.02] border border-white/10 rounded-2xl p-6 backdrop-blur-md">
        <div>
          <Label htmlFor="name" className="text-slate-300 text-xs uppercase tracking-widest">Nome</Label>
          <div className="relative mt-1.5">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="name"
              value={contact.name}
              onChange={e => setContact({ ...contact, name: e.target.value })}
              placeholder="Maria Silva"
              className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-indigo-400"
              data-testid="contact-name"
              autoFocus
            />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-widest">Email profissional</Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="email"
              type="email"
              value={contact.email}
              onChange={e => setContact({ ...contact, email: e.target.value })}
              placeholder="maria@empresa.pt"
              className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-indigo-400"
              data-testid="contact-email"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="company" className="text-slate-300 text-xs uppercase tracking-widest">Empresa</Label>
            <div className="relative mt-1.5">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                id="company"
                value={contact.company}
                onChange={e => setContact({ ...contact, company: e.target.value })}
                placeholder="Acme Lda"
                className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-indigo-400"
                data-testid="contact-company"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="role" className="text-slate-300 text-xs uppercase tracking-widest">Cargo (opcional)</Label>
            <Input
              id="role"
              value={contact.role}
              onChange={e => setContact({ ...contact, role: e.target.value })}
              placeholder="CISO, DPO, CTO…"
              className="mt-1.5 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-indigo-400"
              data-testid="contact-role"
            />
          </div>
        </div>
        <label className="flex items-start gap-2 pt-2 cursor-pointer">
          <Checkbox
            checked={contact.consent_marketing}
            onCheckedChange={(v) => setContact({ ...contact, consent_marketing: !!v })}
            className="mt-0.5 border-slate-500 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
            data-testid="contact-consent"
          />
          <span className="text-xs text-slate-400 leading-relaxed">
            Autorizo receber updates ocasionais sobre EU AI Act e NIS2 (posso cancelar a qualquer momento).
          </span>
        </label>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-sm text-slate-400 hover:text-white transition-colors"
          data-testid="contact-back-btn"
        >
          ← Voltar
        </button>
        <Button
          onClick={onSubmit}
          disabled={!canProceed || submitting}
          className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white rounded-full font-semibold px-8 h-11 disabled:opacity-40"
          data-testid="contact-submit-btn"
        >
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A calcular…</> : <>Ver o meu diagnóstico <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Result
// ============================================================================
function ResultView({ result, onRestart }) {
  const ui = LEVEL_UI[result.level] || LEVEL_UI.medium;
  return (
    <div className="max-w-3xl mx-auto" data-testid="assessment-result">
      <div className="text-center mb-10">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs mb-6 ${ui.badge}`} data-testid="result-level-badge">
          <AlertTriangle className="h-3.5 w-3.5" />
          Nível de exposição · {ui.label}
        </div>
        <div className={`inline-flex items-center justify-center h-40 w-40 rounded-full bg-white/[0.03] border border-white/10 ${ui.glow}`}>
          <div className="text-center">
            <div className={`text-5xl font-bold ${ui.accent}`} data-testid="result-score">{result.score}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">de 100</div>
          </div>
        </div>
        <h2 className="mt-8 text-2xl md:text-3xl font-semibold tracking-tight max-w-xl mx-auto leading-snug">
          {result.headline}
        </h2>
      </div>

      {result.findings?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-4 backdrop-blur-md">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-slate-400 mb-4">Principais achados</h3>
          <ul className="space-y-3">
            {result.findings.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed" data-testid={`result-finding-${i}`}>
                <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${ui.accent}`} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recommendations?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-md">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-slate-400 mb-4">Próximos passos recomendados</h3>
          <ul className="space-y-3">
            {result.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed" data-testid={`result-rec-${i}`}>
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-gradient-to-br from-indigo-500/10 via-fuchsia-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 text-center backdrop-blur-md">
        <Sparkles className="h-8 w-8 mx-auto text-indigo-400 mb-3" />
        <h3 className="text-xl font-semibold">Pronto para ir mais fundo?</h3>
        <p className="mt-2 text-sm text-slate-300 max-w-lg mx-auto">
          O NIS2AI gera automaticamente doc técnica Anexo IV, análise de riscos e um roadmap de conformidade com IA. Vamos preparar-lhe uma demo focada nos gaps que encontrámos.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={`mailto:contacto@nis2ai.pt?subject=Demo%20NIS2AI%20-%20AI%20Act%20(score%20${result.score})&body=Ol%C3%A1%2C%0A%0AFa%C3%A7o%20seguimento%20ao%20diagn%C3%B3stico%20AI%20Act%20com%20score%20${result.score}/100%20(${ui.label}).%20Gostaria%20de%20agendar%20uma%20demo.%0A%0AObrigado.`}
            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold h-11 px-6 text-sm transition-colors"
            data-testid="result-book-demo-btn"
          >
            Agendar demo de 20 min <ArrowRight className="h-4 w-4" />
          </a>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="result-login-link">
            Já sou cliente · entrar →
          </Link>
        </div>
        <div className="mt-4">
          <Link to="/landing/nis2" className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="result-crosslink-nis2">
            Também estão cobertos pela NIS2? Avaliar aqui →
          </Link>
        </div>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={onRestart}
          className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="result-restart-btn"
        >
          ↺ Refazer diagnóstico
        </button>
      </div>
    </div>
  );
}
